import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Card,
  CardContent,
  CardHeader,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  AlertTitle,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Microsoft as MicrosoftIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  Domain as DomainIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import SupervisorService from '../services/Supervisor.Service';

interface OAuthSetupProps {
  open: boolean;
  onClose: () => void;
  onComplete: (config: any) => void;
  initialData?: {
    fromEmail?: string;
    replyEmail?: string;
    oauthProvider?: string;
    oauthTokens?: any;
    oauthExpiry?: string;
    oauthScope?: string;
  };
  isEditMode?: boolean;
  isFirstTimeSetup?: boolean; // New prop to distinguish first-time vs update
}

const OAuthSetup: React.FC<OAuthSetupProps> = ({
  open,
  onClose,
  onComplete,
  initialData,
  isEditMode = false,
  isFirstTimeSetup = false
}) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [emailConfig, setEmailConfig] = useState({
    fromEmail: initialData?.fromEmail || '',
    replyEmail: initialData?.replyEmail || '',
    oauthProvider: initialData?.oauthProvider || 'gmail',
  });

  const [isValidating, setIsValidating] = useState(false);
  const [backendAuthUrl, setBackendAuthUrl] = useState('');
  const [oauthWindow, setOauthWindow] = useState<Window | null>(null);
  const [obtainedOAuthTokens, setObtainedOAuthTokens] = useState<any>(
    isEditMode && initialData?.oauthTokens ? {
      oauthTokens: initialData.oauthTokens,
      oauthExpiry: initialData.oauthExpiry,
      oauthScope: initialData.oauthScope
    } : null
  );

  const steps = [
    'Choose Provider',
    'Configure OAuth',
    'Complete Setup'
  ];

  // Handle edit mode initialization and reset state when modal opens
  useEffect(() => {
    if (open) {
      // Reset state when modal opens
      setActiveStep(0);
      setEmailConfig({
        fromEmail: initialData?.fromEmail || '',
        replyEmail: initialData?.replyEmail || '',
        oauthProvider: initialData?.oauthProvider || 'gmail',
      });
      setIsValidating(false);
      setBackendAuthUrl('');
      setOauthWindow(null);
      setObtainedOAuthTokens(
        isEditMode && initialData?.oauthTokens ? {
          oauthTokens: initialData.oauthTokens,
          oauthExpiry: initialData.oauthExpiry,
          oauthScope: initialData.oauthScope
        } : null
      );

      if (isEditMode && initialData?.oauthTokens) {
        // Skip to step 1 (Configure OAuth) if we have existing tokens
        setActiveStep(1);
      }
    }
  }, [open, isEditMode, initialData]);

  // Cleanup popup window on unmount
  useEffect(() => {
    return () => {
      if (oauthWindow && !oauthWindow.closed) {
        oauthWindow.close();
      }
    };
  }, [oauthWindow]);

  const providers = [
    {
      value: 'gmail',
      label: 'Gmail / Google Workspace',
      icon: <GoogleIcon sx={{ color: '#4285F4' }} />,
      description: 'Use Gmail or Google Workspace email'
    },
    {
      value: 'outlook',
      label: 'Outlook / Office 365',
      icon: <MicrosoftIcon sx={{ color: '#0078D4' }} />,
      description: 'Use Outlook or Office 365 email'
    },
    {
      value: 'yahoo',
      label: 'Yahoo Mail',
      icon: <EmailIcon sx={{ color: '#7B0099' }} />,
      description: 'Use Yahoo Mail account'
    }
  ];

  const handleNext = () => {
    // Validate email fields when moving from step 0 to step 1
    if (activeStep === 0) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      return;
    }

    // Validate email fields when moving from step 1 to step 2
    if (activeStep === 1) {
      if (!emailConfig.fromEmail.trim()) {
        toast.error('From Email is required');
        return;
      }
      if (!emailConfig.replyEmail.trim()) {
        toast.error('Reply-To Email is required');
        return;
      }
      if (!emailConfig.fromEmail.includes('@')) {
        toast.error('Please enter a valid From Email address');
        return;
      }
      if (!emailConfig.replyEmail.includes('@')) {
        toast.error('Please enter a valid Reply-To Email address');
        return;
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const initiateOAuthSetup = async () => {
    if (!emailConfig.fromEmail.trim()) {
      toast.error('From Email is required');
      return;
    }
    if (!emailConfig.replyEmail.trim()) {
      toast.error('Reply-To Email is required');
      return;
    }
    if (!emailConfig.fromEmail.includes('@')) {
      toast.error('Please enter a valid From Email address');
      return;
    }
    if (!emailConfig.replyEmail.includes('@')) {
      toast.error('Please enter a valid Reply-To Email address');
      return;
    }

    setIsValidating(true);
    try {
      // Call the backend to initiate OAuth setup
      const config = {
        fromEmail: emailConfig.fromEmail,
        replyEmail: emailConfig.replyEmail,
        emailMethod: 'oauth',
        oauthProvider: emailConfig.oauthProvider,
      };

      // Make the actual API call based on setup type
      let resp;
      if (isFirstTimeSetup) {
        // First-time OAuth setup
        resp = await SupervisorService.setUpAnyEmailDomain(config);
      } else {
        // Update existing OAuth configuration
        resp = await SupervisorService.updateEmailSetup(config);
      }

      // Check if we got an authUrl from the backend
      if (resp.data?.authUrl) {
        setBackendAuthUrl(resp.data.authUrl);
        // Auto-advance to step 3 (Complete OAuth Authorization) when URL is generated
        setActiveStep(2);
      } else {
        handleComplete(config);
      }
    } catch (error) {
      console.error('OAuth setup error:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const resetComponentState = () => {
    setActiveStep(0);
    setEmailConfig({
      fromEmail: initialData?.fromEmail || '',
      replyEmail: initialData?.replyEmail || '',
      oauthProvider: initialData?.oauthProvider || 'gmail',
    });
    setIsValidating(false);
    setBackendAuthUrl('');
    setOauthWindow(null);
    setObtainedOAuthTokens(
      isEditMode && initialData?.oauthTokens ? {
        oauthTokens: initialData.oauthTokens,
        oauthExpiry: initialData.oauthExpiry,
        oauthScope: initialData.oauthScope
      } : null
    );
  };

  const handleComplete = (config: any) => {
    onComplete({
      ...emailConfig,
      ...config
    });
    resetComponentState();
    onClose();
  };

  const openOAuthPopup = () => {
    if (!backendAuthUrl) return;

    // Close existing popup if any
    if (oauthWindow && !oauthWindow.closed) {
      oauthWindow.close();
    }

    // Open new popup window
    const popup = window.open(
      backendAuthUrl,
      'oauth-popup',
      'width=600,height=700,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );

    if (popup) {
      setOauthWindow(popup);

      // Focus the popup window
      popup.focus();

      // Check if popup is closed periodically
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setOauthWindow(null);
          // You can add additional logic here when popup is closed
        }
      }, 1000);
    } else {
      // Fallback to new tab if popup is blocked
      window.open(backendAuthUrl, '_blank');
    }
  };

  const handleOAuthComplete = () => {
    resetComponentState();
    onClose();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Email Provider
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose your email provider to configure OAuth authentication.
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={emailConfig.oauthProvider}
                onChange={(e) => setEmailConfig({ ...emailConfig, oauthProvider: e.target.value })}
              >
                {providers.map((provider) => (
                  <Card
                    key={provider.value}
                    sx={{
                      mb: 2,
                      border: emailConfig.oauthProvider === provider.value ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
                      cursor: 'pointer'
                    }}
                    onClick={() => setEmailConfig({ ...emailConfig, oauthProvider: provider.value })}
                  >
                    <CardContent>
                      <FormControlLabel
                        value={provider.value}
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {provider.icon}
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {provider.label}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {provider.description}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        sx={{ width: '100%', m: 0 }}
                      />
                    </CardContent>
                  </Card>
                ))}
              </RadioGroup>
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {isFirstTimeSetup ? 'Set Up OAuth Authentication' : 'Update OAuth Configuration'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {isFirstTimeSetup
                ? `Set up OAuth authentication for ${emailConfig.oauthProvider} to enable email functionality.`
                : `Update your OAuth configuration for ${emailConfig.oauthProvider}. You can modify email addresses or refresh OAuth tokens.`
              }
            </Typography>

            {isEditMode && obtainedOAuthTokens && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <AlertTitle>Current Configuration</AlertTitle>
                <Typography variant="body2">
                  <strong>Provider:</strong> {emailConfig.oauthProvider}<br />
                  <strong>From Email:</strong> {emailConfig.fromEmail}<br />
                  <strong>Reply-To:</strong> {emailConfig.replyEmail}<br />
                  <strong>Status:</strong> OAuth tokens are configured
                </Typography>
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="From Email Address"
                type="email"
                value={emailConfig.fromEmail}
                onChange={(e) => setEmailConfig({ ...emailConfig, fromEmail: e.target.value })}
                placeholder="your-email@example.com"
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                label="Reply-To Email Address"
                type="email"
                value={emailConfig.replyEmail}
                onChange={(e) => setEmailConfig({ ...emailConfig, replyEmail: e.target.value })}
                placeholder="reply-to@example.com"
              />
            </Box>

            {isEditMode && obtainedOAuthTokens && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <AlertTitle>OAuth tokens are already configured</AlertTitle>
                  You can keep the existing OAuth tokens or refresh them below.
                </Alert>
                <Button
                  variant="outlined"
                  onClick={handleNext}
                  sx={{ mb: 2 }}
                >
                  Keep Existing Tokens & Continue
                </Button>
              </Box>
            )}

            {emailConfig.oauthProvider === 'gmail' && (
              <Card>
                <CardHeader
                  avatar={<GoogleIcon sx={{ color: '#4285F4' }} />}
                  title="Configure Gmail OAuth"
                />
                <CardContent>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>{isFirstTimeSetup ? 'OAuth Setup Process' : 'OAuth Update Process'}</AlertTitle>
                    {isFirstTimeSetup
                      ? 'Click the button below to initiate OAuth setup. The system will generate an authorization URL for you to complete the Gmail authentication.'
                      : 'Click the button below to update your OAuth configuration. You may need to re-authorize Gmail access if tokens have expired.'
                    }
                  </Alert>

                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>Required Scopes:</AlertTitle>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <li>✅ https://www.googleapis.com/auth/gmail.readonly</li>
                      <li>✅ https://www.googleapis.com/auth/gmail.modify</li>
                    </Box>
                  </Alert>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    Login with: <strong>{emailConfig.fromEmail}</strong>
                  </Alert>

                  <Button
                    variant="contained"
                    onClick={initiateOAuthSetup}
                    disabled={isValidating}
                    startIcon={isValidating ? <CircularProgress size={20} /> : <SecurityIcon />}
                    sx={{ mb: 2 }}
                  >
                    {isValidating
                      ? (isFirstTimeSetup ? 'Setting up OAuth...' : 'Updating OAuth...')
                      : (isFirstTimeSetup ? 'Set Up OAuth' : 'Update OAuth Configuration')
                    }
                  </Button>

                  {backendAuthUrl && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <AlertTitle>✅ Configuration Saved Successfully!</AlertTitle>
                      <Typography variant="body2">
                        Your OAuth configuration has been saved. Click "Next" to proceed to authorization.
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            )}

            {emailConfig.oauthProvider === 'outlook' && (
              <Card>
                <CardHeader
                  avatar={<MicrosoftIcon sx={{ color: '#0078D4' }} />}
                  title="Configure Outlook OAuth"
                />
                <CardContent>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>{isFirstTimeSetup ? 'OAuth Setup Process' : 'OAuth Update Process'}</AlertTitle>
                    {isFirstTimeSetup
                      ? 'Click the button below to initiate OAuth setup. The system will generate an authorization URL for you to complete the Microsoft Outlook authentication.'
                      : 'Click the button below to update your OAuth configuration. You may need to re-authorize Outlook access if tokens have expired.'
                    }
                  </Alert>

                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>Required Scopes:</AlertTitle>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <li>✅ https://graph.microsoft.com/Mail.Read</li>
                      <li>✅ https://graph.microsoft.com/Mail.ReadWrite</li>
                    </Box>
                  </Alert>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    Login with: <strong>{emailConfig.fromEmail}</strong>
                  </Alert>

                  <Button
                    variant="contained"
                    onClick={initiateOAuthSetup}
                    disabled={isValidating}
                    startIcon={isValidating ? <CircularProgress size={20} /> : <SecurityIcon />}
                    sx={{ mb: 2 }}
                  >
                    {isValidating
                      ? (isFirstTimeSetup ? 'Setting up OAuth...' : 'Updating OAuth...')
                      : (isFirstTimeSetup ? 'Set Up OAuth' : 'Update OAuth Configuration')
                    }
                  </Button>

                  {backendAuthUrl && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <AlertTitle>✅ Configuration Saved Successfully!</AlertTitle>
                      <Typography variant="body2">
                        Your OAuth configuration has been saved. Click "Next" to proceed to authorization.
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            )}

            {emailConfig.oauthProvider === 'yahoo' && (
              <Card>
                <CardHeader
                  avatar={<EmailIcon sx={{ color: '#7B0099' }} />}
                  title="Configure Yahoo OAuth"
                />
                <CardContent>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>{isFirstTimeSetup ? 'OAuth Setup Process' : 'OAuth Update Process'}</AlertTitle>
                    {isFirstTimeSetup
                      ? 'Click the button below to initiate OAuth setup. The system will generate an authorization URL for you to complete the Yahoo Mail authentication.'
                      : 'Click the button below to update your OAuth configuration. You may need to re-authorize Yahoo Mail access if tokens have expired.'
                    }
                  </Alert>

                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>Required Scopes:</AlertTitle>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <li>✅ mail-r (Read mail)</li>
                      <li>✅ mail-w (Write mail)</li>
                    </Box>
                  </Alert>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    Login with: <strong>{emailConfig.fromEmail}</strong>
                  </Alert>

                  <Button
                    variant="contained"
                    onClick={initiateOAuthSetup}
                    disabled={isValidating}
                    startIcon={isValidating ? <CircularProgress size={20} /> : <SecurityIcon />}
                    sx={{ mb: 2 }}
                  >
                    {isValidating
                      ? (isFirstTimeSetup ? 'Setting up OAuth...' : 'Updating OAuth...')
                      : (isFirstTimeSetup ? 'Set Up OAuth' : 'Update OAuth Configuration')
                    }
                  </Button>

                  {backendAuthUrl && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <AlertTitle>✅ Configuration Saved Successfully!</AlertTitle>
                      <Typography variant="body2">
                        Your OAuth configuration has been saved. Click "Next" to proceed to authorization.
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            )}


            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button variant="outlined" onClick={handleBack}>
                Back
              </Button>
              <Button variant="contained" onClick={handleNext} disabled={!backendAuthUrl && !obtainedOAuthTokens}>
                Next
              </Button>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Complete OAuth Authorization
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Complete OAuth Authorization</AlertTitle>
              Your email configuration has been saved. Please complete the OAuth authorization process to enable email functionality.
            </Alert>

            {backendAuthUrl && (
              <Alert severity="success" sx={{ mb: 3 }}>
                <AlertTitle>✅ Authorization URL Ready</AlertTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Chip label="Click to authorize" size="small" color="success" />
                  <IconButton
                    size="small"
                    onClick={() => navigator.clipboard.writeText(backendAuthUrl)}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                  <Button
                    variant="contained"
                    startIcon={<OpenInNewIcon />}
                    onClick={openOAuthPopup}
                  >
                    Complete OAuth Authorization
                  </Button>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  After completing authorization, your email will be automatically configured.
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button variant="outlined" onClick={handleBack}>
                Back
              </Button>
              <Button variant="contained" onClick={handleOAuthComplete}>
                Complete
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  const handleClose = () => {
    if (!isValidating) {
      // Close popup window if open
      if (oauthWindow && !oauthWindow.closed) {
        oauthWindow.close();
      }
      resetComponentState();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isEditMode ? 'Edit OAuth Email Configuration' : 'OAuth Email Setup'}
        <IconButton onClick={handleClose} size="small" disabled={isValidating}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>
    </Dialog>
  );
};

export default OAuthSetup;